// super-excalidraw: thin client for the /api/media endpoints (the user's
// personal media library). Same trust model as everything else: descriptions
// are intended to be the LLM's hint for "when to use this image".

export type MediaItemDTO = {
  id: string;
  name?: string;
  description?: string;
  fileId: string;
  mimeType?: string;
  width?: number;
  height?: number;
  folderId?: string;
  addedAt: number;
};

export type MediaLibraryDTO = {
  items: MediaItemDTO[];
  updatedAt?: number;
};

const base = "/api/media";

export async function fetchMediaLibrary(): Promise<MediaLibraryDTO> {
  const r = await fetch(base + "/");
  if (!r.ok) {
    throw new Error(`media fetch ${r.status}`);
  }
  return r.json();
}

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const i = result.indexOf(",");
      resolve(i >= 0 ? result.substring(i + 1) : "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const probeImageDimensions = async (file: File): Promise<{ width: number; height: number }> => {
  if (!file.type.startsWith("image/")) {
    return { width: 0, height: 0 };
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
};

export async function uploadMedia(opts: {
  file: File;
  name: string;
  description: string;
}): Promise<MediaItemDTO> {
  const dataBase64 = await blobToBase64(opts.file);
  const dims = await probeImageDimensions(opts.file);
  const r = await fetch(base + "/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: opts.name,
      description: opts.description,
      mimeType: opts.file.type || "application/octet-stream",
      dataBase64,
      width: dims.width,
      height: dims.height,
    }),
  });
  if (!r.ok) {
    throw new Error(`media upload ${r.status}`);
  }
  return r.json();
}

export async function patchMedia(
  id: string,
  patch: { name?: string; description?: string; folderId?: string },
): Promise<void> {
  const r = await fetch(`${base}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!r.ok) {
    throw new Error(`media patch ${r.status}`);
  }
}

export async function deleteMedia(id: string): Promise<void> {
  const r = await fetch(`${base}/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!r.ok) {
    throw new Error(`media delete ${r.status}`);
  }
}

// Returns the URL the canvas (and Claude/AI) can use to fetch raw bytes.
// We stream from `/api/media/:id/file` — a dedicated path that always
// returns the original bytes (no Excalidraw encryption envelope), unlike
// the shared `/api/files/:id` path used for diagram-embedded images.
export function mediaUrl(item: MediaItemDTO): string {
  return `/api/media/${encodeURIComponent(item.id)}/file`;
}
